import { useEffect, useRef, useState, useCallback } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "./ui/button";
import { Camera, RefreshCw, VideoOff, XCircle, HelpCircle } from "lucide-react";
import CameraPermissionGuide from "./CameraPermissionGuide";

function humanError(e) {
  const msg = (e && (e.message || e.name)) ? (e.message || e.name) : String(e);
  const isIOS = /iP(hone|ad|od)/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  if (/permission|NotAllowed|denied/i.test(msg)) {
    let howTo;
    if (isIOS) {
      howTo = "Vá a Definições → Safari → Câmara → Permitir. Depois volte à app e recarregue a página.";
    } else if (isAndroid) {
      howTo = "Toque no cadeado (🔒) na barra de endereço → Permissões → Câmara → Permitir. Depois recarregue.";
    } else {
      howTo = "Clique no ícone da câmara/cadeado na barra de endereço, escolha 'Permitir' e recarregue a página.";
    }
    return { title: "Permissão negada", detail: howTo, code: "permission" };
  }
  if (/NotFound|Requested device|OverconstrainedError/i.test(msg))
    return { title: "Câmara não encontrada", detail: "Verifique se existe uma câmara ligada e disponível.", code: "not_found" };
  if (/NotReadable|TrackStart|already in use/i.test(msg))
    return { title: "Câmara em uso", detail: "Feche outras apps que estejam a usar a câmara (ex: Zoom, Teams) e recarregue.", code: "in_use" };
  if (/secure|https|SecurityError/i.test(msg))
    return { title: "HTTPS necessário", detail: "A câmara só funciona em conexões HTTPS.", code: "https" };
  return { title: "Erro na câmara", detail: msg, code: "unknown" };
}

/**
 * Reusable QR Scanner:
 *  - Primes permission with navigator.mediaDevices.getUserMedia
 *  - Lists available cameras and lets user switch
 *  - Uses explicit deviceId (more reliable than facingMode)
 *  - Auto-starts on desktop; requires "Iniciar câmara" button on iOS Safari (user gesture)
 */
export default function QrScanner({
  onDecode,
  size = 320,
  borderColor = "#f97316",
  autoStart = true,
  scannerId = "qr-scanner-region",
  className = "",
  testId,
}) {
  const [cameras, setCameras] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const scannerRef = useRef(null);
  const submittedRef = useRef(null);
  const guideShownRef = useRef(false);

  // Probe permission & enumerate cameras
  const probeAndListCameras = useCallback(async () => {
    setError(null);
    setStarting(true);
    try {
      // Prime permission with user gesture (call from a click on iOS)
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      // Release the primer immediately; Html5Qrcode will re-open the stream
      stream.getTracks().forEach(t => t.stop());
      // List cameras
      const list = await Html5Qrcode.getCameras();
      if (!list || list.length === 0) {
        throw new Error("Nenhuma câmara detectada");
      }
      setCameras(list);
      // Prefer back camera by label
      const back = list.find(c => /back|rear|environment|traseira/i.test(c.label)) || list[list.length - 1];
      setSelectedId(back.id);
      return back.id;
    } catch (e) {
      const err = humanError(e);
      setError(err);
      // Auto-open guide on first permission denial only
      if (err.code === "permission" && !guideShownRef.current) {
        guideShownRef.current = true;
        setGuideOpen(true);
      }
      return null;
    } finally {
      setStarting(false);
    }
  }, []);

  const stopScanner = useCallback(async () => {
    const s = scannerRef.current;
    if (!s) return;
    try {
      if (s._isScanning || s.isScanning) {
        await s.stop();
      }
      await s.clear();
    } catch { /* ignore */ }
    scannerRef.current = null;
    setRunning(false);
  }, []);

  const startScanner = useCallback(async (deviceId) => {
    let camId = deviceId || selectedId;
    if (!camId) {
      camId = await probeAndListCameras();
      if (!camId) return;
    }
    await stopScanner();
    setStarting(true);
    setError(null);
    try {
      // Ensure DOM element exists
      if (!document.getElementById(scannerId)) {
        throw new Error("Contentor da câmara não está pronto");
      }
      const scanner = new Html5Qrcode(scannerId, { verbose: false });
      scannerRef.current = scanner;
      await scanner.start(
        camId,
        { fps: 12, qrbox: { width: Math.min(size, 320), height: Math.min(size, 320) }, aspectRatio: 1.0 },
        (decoded) => {
          if (submittedRef.current === decoded) return;
          submittedRef.current = decoded;
          setTimeout(() => { submittedRef.current = null; }, 3000);
          if (onDecode) onDecode(decoded);
        },
        () => { /* silent per-frame errors */ }
      );
      setRunning(true);
    } catch (e) {
      setError(humanError(e));
      setRunning(false);
    } finally {
      setStarting(false);
    }
  }, [selectedId, size, scannerId, onDecode, probeAndListCameras, stopScanner]);

  // Auto-start ONLY on desktop; on any mobile/tablet require user gesture (avoids browsers silently denying).
  useEffect(() => {
    if (!autoStart || running) return;
    const ua = navigator.userAgent;
    const isMobile = /iP(hone|ad|od)|Android|Mobile|Tablet/i.test(ua);
    if (isMobile) return;
    (async () => {
      const camId = await probeAndListCameras();
      if (camId) startScanner(camId);
    })();
  }, [autoStart]);

  // Cleanup on unmount
  useEffect(() => () => { stopScanner(); }, [stopScanner]);

  const handleChangeCamera = async (id) => {
    setSelectedId(id);
    await startScanner(id);
  };

  return (
    <div className={`flex flex-col items-center gap-3 ${className}`} data-testid={testId}>
      <div
        className="relative rounded-xl overflow-hidden bg-black"
        style={{ width: size, height: size, border: `3px solid ${borderColor}` }}
      >
        {/* Scanner region — must have no React children so html5-qrcode can safely manipulate DOM */}
        <div
          id={scannerId}
          data-testid={testId ? `${testId}-region` : undefined}
          style={{ width: "100%", height: "100%" }}
        />
        {/* Overlays (positioned above scanner via z-index) */}
        {!running && !error && !starting && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-400 text-sm text-center px-4">
            <div>
              <Camera className="h-10 w-10 mx-auto mb-2 opacity-50" />
              Toque em &quot;Iniciar câmara&quot; para começar
            </div>
          </div>
        )}
        {starting && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-slate-300 text-sm text-center px-4 animate-pulse">
            A pedir permissão...
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/85 text-red-200 text-center px-3 py-4 overflow-y-auto">
            <div>
              <VideoOff className="h-9 w-9 mx-auto mb-2 text-red-400" />
              <div className="font-semibold text-sm mb-1 text-white">{error.title}</div>
              <div className="text-xs opacity-90 leading-relaxed">{error.detail}</div>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex flex-wrap gap-2 justify-center items-center">
        {!running && (
          <Button
            type="button"
            size="sm"
            onClick={() => startScanner()}
            disabled={starting}
            data-testid={testId ? `${testId}-start` : "qr-start"}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <Camera className="h-4 w-4 mr-1" />
            {starting ? "A iniciar..." : "Iniciar câmara"}
          </Button>
        )}
        {running && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={stopScanner}
            data-testid={testId ? `${testId}-stop` : "qr-stop"}
          >
            <XCircle className="h-4 w-4 mr-1" /> Parar
          </Button>
        )}
        {cameras.length > 1 && (
          <select
            className="text-sm border rounded px-2 py-1 bg-white"
            value={selectedId}
            onChange={(e) => handleChangeCamera(e.target.value)}
            data-testid={testId ? `${testId}-select` : "qr-select"}
          >
            {cameras.map(c => (
              <option key={c.id} value={c.id}>{c.label || `Câmara ${c.id.slice(0, 6)}`}</option>
            ))}
          </select>
        )}
        {error && (
          <>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => startScanner()}
              data-testid={testId ? `${testId}-retry` : "qr-retry"}
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Tentar de novo
            </Button>
            {error.code === "permission" && (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setGuideOpen(true)}
                  data-testid={testId ? `${testId}-help` : "qr-help"}
                  className="border-blue-300 text-blue-700"
                >
                  <HelpCircle className="h-4 w-4 mr-1" /> Como desbloquear?
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => window.location.reload()}
                  data-testid={testId ? `${testId}-reload` : "qr-reload"}
                  className="border-orange-300 text-orange-700"
                >
                  <RefreshCw className="h-4 w-4 mr-1" /> Recarregar página
                </Button>
              </>
            )}
          </>
        )}
      </div>

      <CameraPermissionGuide open={guideOpen} onOpenChange={setGuideOpen} />
    </div>
  );
}
